import { File, FileText, Image } from 'lucide-react'
import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE,
  UPLOAD_MESSAGES,
} from '../config'

/**
 * Get appropriate icon for file type
 */
export const getFileIcon = (fileType: string) => {
  if (fileType.startsWith('image/')) {
    return <Image className="w-5 h-5 text-primary" />
  }
  if (fileType === 'application/pdf') {
    return <FileText className="w-5 h-5 text-primary" />
  }
  return <File className="w-5 h-5 text-primary" />
}

/**
 * Get appropriate icon for file type (smaller version for picker)
 */
export const getFileIconSmall = (fileType: string) => {
  if (fileType.startsWith('image/')) {
    return <Image className="w-4 h-4 text-primary" />
  }
  if (fileType === 'application/pdf') {
    return <FileText className="w-4 h-4 text-primary" />
  }
  return <File className="w-4 h-4 text-primary" />
}

/**
 * Format file size in human readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Calculate total size of multiple files
 */
export const formatTotalSize = (files: Array<File>): string => {
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0)
  return formatFileSize(totalBytes)
}

/**
 * Validate file type against allowed types
 */
export const isFileTypeAllowed = (
  fileType: string,
  allowedTypes: Array<string>,
): boolean => {
  return allowedTypes.includes(fileType)
}

/**
 * Check if file already exists in array
 */
export const isFileDuplicate = (
  file: File,
  existingFiles: Array<File>,
): boolean => {
  return existingFiles.some((f) => f.name === file.name && f.size === file.size)
}

export interface ValidatedFiles {
  validFiles: Array<File>
  errors: Array<string>
}

/**
 * Filter incoming files against the attachment allowlist, size cap, and
 * existing-file duplicates. Returns the accepted files and a list of
 * human-readable rejection reasons. Dedupes within `incoming` as well so two
 * pastes of the same screenshot don't both pass.
 */
export const validateFiles = (
  incoming: Array<File>,
  existingFiles: Array<File>,
): ValidatedFiles => {
  const validFiles: Array<File> = []
  const errors: Array<string> = []

  incoming.forEach((file) => {
    if (!isFileTypeAllowed(file.type, ALLOWED_FILE_TYPES)) {
      errors.push(`${file.name}: ${UPLOAD_MESSAGES.TYPE_NOT_ALLOWED}`)
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      errors.push(`${file.name}: ${UPLOAD_MESSAGES.SIZE_EXCEEDED}`)
      return
    }
    if (
      isFileDuplicate(file, existingFiles) ||
      isFileDuplicate(file, validFiles)
    ) {
      errors.push(`${file.name}: ${UPLOAD_MESSAGES.ALREADY_EXISTS}`)
      return
    }
    validFiles.push(file)
  })

  return { validFiles, errors }
}
